import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import usdtLogo from '@/assets/logos/usdt.png';

import classNames from 'classnames';
import { calculateRewardMultiplier } from '@/utils/calculateRewardMultiplier';

import { RaceData, Token } from '@/pages/race-wizard';

const RaceReviewModal = ({
  open,
  onOpenChange,
  raceData,
  selectedTokens,
  buttonText,
  isSubmitDisabled,
  getTransactionAction,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  raceData: RaceData;
  selectedTokens: Token[];
  buttonText: string;
  isSubmitDisabled: boolean;
  getTransactionAction: () => void | Promise<void>;
}) => {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[9999] bg-black/60 blur-2xl" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[10000] w-[95vw] max-w-[394px] -translate-x-1/2 -translate-y-1/2 space-y-6 rounded-3xl border border-[#282133] bg-[#10091C] p-8 outline-none">
          <Dialog.Title className="text-lg font-bold">Review Race Information</Dialog.Title>
          <Dialog.Description className="sr-only !my-0">
            Please review the race information below and confirm to submit.
          </Dialog.Description>

          <div className="!mt-3 space-y-3">
            <div>
              <span className="text-xs text-[#AEAEB2]">Race Name</span>
              <div>{raceData?.raceName}</div>
            </div>

            <div>
              <span className="text-xs text-[#AEAEB2]">Race Duration</span>
              <div>{raceData?.raceDuration} hours</div>
            </div>

            <div>
              <span className="text-xs text-[#AEAEB2]">Race Start Time</span>
              <div>
                {raceData.startDateTime?.format('DD MMMM YYYY')} at {raceData.startDateTime?.format('hh:mm A')}
              </div>
            </div>

            <div>
              <div className="text-xs text-[#AEAEB2]">Handicap</div>
              <div>{raceData?.handicapSettings?.enabled ? 'Yes' : 'No'}</div>
            </div>

            <div>
              <span className="text-xs text-[#AEAEB2]">Total Staking Reward Pool</span>
              <div className="flex items-center gap-2">
                <img src={usdtLogo} alt="USDT" className="size-4" />
                <div>{raceData?.usdcAmount}</div>
              </div>
            </div>

            <div className="space-y-1">
              <span className="text-xs text-[#AEAEB2]">Potential Staking Reward Multiplier</span>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                {selectedTokens.map((token, index) => {
                  const isNotLastItem = index !== selectedTokens.length - 1;
                  const simplifiedRatio = calculateRewardMultiplier(
                    raceData?.usdcAmount || 0,
                    raceData?.allocations?.[token.symbol] || 0
                  );
                  return (
                    <React.Fragment key={token.id}>
                      <div className="flex flex-col items-center gap-2">
                        <img src={token.image} alt={token.name} className="size-8 rounded-full" />
                        <div>{simplifiedRatio}</div>
                      </div>
                      {isNotLastItem && <div className="font-medium">VS</div>}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex gap-2 *:w-full *:grow">
            <button
              onClick={() => onOpenChange(false)}
              className="rounded-lg border border-[#282133] p-2 font-bold lg:min-w-40"
            >
              Back
            </button>
            <button
              disabled={isSubmitDisabled}
              onClick={getTransactionAction}
              className={classNames('rounded-lg p-2 font-bold disabled:bg-[#282133] disabled:opacity-70 lg:min-w-40', {
                'bg-[#282133] opacity-70': buttonText === 'Connect Wallet',
                'bg-[#9937F7]': buttonText !== 'Connect Wallet',
              })}
            >
              {buttonText}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default RaceReviewModal;
